import { type ReactNode, type Ref, useImperativeHandle, useMemo } from 'react';

import { DynamicOverflowContext, type DynamicOverflowContextData } from './DynamicOverflowContext';
import type { GetVisibleElements, OverflowElement } from './types';
import { useDynamicOverflow } from './useDynamicOverflow';

interface DynamicOverflowProps<T> {
  getVisibleElements: GetVisibleElements<T>;
  children: (props: { overflowElements: OverflowElement<T>[] }) => ReactNode;
  forwardRef?: Ref<{ refresh: () => void }>;
}

export const DynamicOverflow = <T,>({
  getVisibleElements,
  children,
  forwardRef,
}: DynamicOverflowProps<T>) => {
  const { registerElement, isElementHidden, overflowElements, refresh } = useDynamicOverflow<T>({
    getVisibleElements,
  });

  useImperativeHandle(forwardRef, () => ({ refresh }), [refresh]);

  const context = useMemo(
    () => ({
      registerElement,
      isElementHidden,
      refresh,
    }),
    [isElementHidden, registerElement, refresh],
  ) as DynamicOverflowContextData<unknown>;

  return (
    <DynamicOverflowContext.Provider value={context}>
      {children({ overflowElements })}
    </DynamicOverflowContext.Provider>
  );
};
